import { useAtomValue, useSetAtom } from 'jotai'
import { useCallback } from 'react'
import { ImageCropGuide } from '../../rapid-ui'
import { IMAGE_CROP_SCALE_MAX, IMAGE_CROP_SCALE_MIN } from '../../utilities'
import { LANDING_PAGE_BANNER_ASPECT_RATIO, LANDING_PAGE_BANNER_MAX_WIDTH_PX } from '../../views'
import { useImageCache } from '../images'
import {
	landingPageImageIdAtom,
	landingPageMetaImageOffsetXAtom,
	landingPageMetaImageOffsetYAtom,
	landingPageMetaImageScaleAtom,
} from '../state'
import { trackLandingPageEdited } from '../tracking'

// The guide reads these as a ratio only, so they need not match any rendered size.
const GUIDE_FRAME_WIDTH = 1600
const GUIDE_FRAME_HEIGHT = GUIDE_FRAME_WIDTH / LANDING_PAGE_BANNER_ASPECT_RATIO

/**
 * Mounted as the backdrop layer's controls pre-launch, and on its own in the
 * post-launch shell, which has no layer manager. Renders no heading of its own —
 * both callers already label the surface it sits in.
 */
export function LandingPageCropControl() {
	const { getImage } = useImageCache()

	const landingPageImageId = useAtomValue(landingPageImageIdAtom)
	const imageScale = useAtomValue(landingPageMetaImageScaleAtom)
	const imageOffsetX = useAtomValue(landingPageMetaImageOffsetXAtom)
	const imageOffsetY = useAtomValue(landingPageMetaImageOffsetYAtom)
	const setImageScale = useSetAtom(landingPageMetaImageScaleAtom)
	const setImageOffsetX = useSetAtom(landingPageMetaImageOffsetXAtom)
	const setImageOffsetY = useSetAtom(landingPageMetaImageOffsetYAtom)

	const handleOffsetChange = useCallback((offsetX: number, offsetY: number) => {
		setImageOffsetX(offsetX)
		setImageOffsetY(offsetY)
		trackLandingPageEdited('image_crop')
	}, [setImageOffsetX, setImageOffsetY])

	const handleScaleChange = useCallback((scale: number) => {
		setImageScale(scale)
		trackLandingPageEdited('image_crop')
	}, [setImageScale])

	const { url: imageUrl } = getImage(landingPageImageId ?? '')

	if (!imageUrl) {
		return null
	}

	return (
		<ImageCropGuide
			src={imageUrl}
			imageScale={imageScale ?? IMAGE_CROP_SCALE_MIN}
			imageOffsetX={imageOffsetX ?? 50}
			imageOffsetY={imageOffsetY ?? 50}
			containerWidth={GUIDE_FRAME_WIDTH}
			containerHeight={GUIDE_FRAME_HEIGHT}
			width="fill"
			minScale={IMAGE_CROP_SCALE_MIN}
			maxScale={IMAGE_CROP_SCALE_MAX}
			displayedWidthPx={LANDING_PAGE_BANNER_MAX_WIDTH_PX}
			onOffsetChange={handleOffsetChange}
			onScaleChange={handleScaleChange}
		/>
	)
}
